import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { FiEye, FiEyeOff, FiUser, FiLock } from 'react-icons/fi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getFormErrorMessage } from '../utils/helpers';
import { USER_ROLES } from '../utils/constants';

const Login = () => {
  const { login, isAuthenticated, isLoading, error, clearError, user } = useAuth();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // Listen for auth errors from API interceptor
  useEffect(() => {
    const handleAuthError = (event) => {
      setAuthErrorMessage(event.detail.message);
      // Clear message after 5 seconds
      setTimeout(() => setAuthErrorMessage(''), 5000);
    };

    window.addEventListener('auth-error', handleAuthError);
    return () => window.removeEventListener('auth-error', handleAuthError);
  }, []);

  // Clear error when user types in the form
  const username = watch('username');
  const password = watch('password');
  useEffect(() => {
    if (error) {
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, password]);

  // Redirect if already authenticated
  if (isAuthenticated) {
    // Check if there's a previous location to redirect to
    const from = location.state?.from?.pathname;

    // If there's a "from" location, use it
    if (from) {
      return <Navigate to={from} replace />;
    }

    // Otherwise, redirect based on user role
    if (user?.role === USER_ROLES.PLATFORM_ADMIN) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user?.role === USER_ROLES.CLEANING_STAFF) {
      return <Navigate to="/orders/mobile" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const result = await login({
        username: data.username,
        password: data.password,
      });

      if (result.success) {
        // Navigation will happen automatically via the useEffect in AuthContext
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="Verificando sesión..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Card Container */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8">
            <div className="flex justify-center mb-4">
              <img
                src="/favicon.svg"
                alt="Teonila Logo"
                className="h-20 w-20 object-contain rounded-full"
              />
            </div>
            <h2 className="text-center text-2xl font-bold text-white">
              Iniciar Sesión
            </h2>
            <p className="mt-2 text-center text-sm text-blue-100">
              Teonila HouseKeeping Management
            </p>
          </div>

          {/* Form Section */}
          <div className="px-6 py-8">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                {/* Username */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                    Usuario
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiUser className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="username"
                      type="text"
                      autoComplete="username"
                      className={`form-input pl-10 ${
                        errors.username ? 'border-danger-500 focus:border-danger-500' : ''
                      }`}
                      placeholder="Ingresa tu usuario"
                      {...register('username', {
                        required: true,
                        minLength: 3,
                      })}
                    />
                  </div>
                  {errors.username && (
                    <p className="mt-1 text-sm text-danger-600">
                      {getFormErrorMessage(errors, 'username')}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Contraseña
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={`form-input pl-10 pr-10 ${
                        errors.password ? 'border-danger-500 focus:border-danger-500' : ''
                      }`}
                      placeholder="Ingresa tu contraseña"
                      {...register('password', {
                        required: true,
                        minLength: 6,
                      })}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <FiEyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                      ) : (
                        <FiEye className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-danger-600">
                      {getFormErrorMessage(errors, 'password')}
                    </p>
                  )}
                </div>
              </div>

              {/* Auth error message from API interceptor */}
              {authErrorMessage && (
                <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
                  <div className="text-sm text-yellow-800">
                    {authErrorMessage}
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="rounded-lg bg-danger-50 border border-danger-200 p-4">
                  <div className="text-sm text-danger-700">
                    {error}
                  </div>
                </div>
              )}

              {/* Submit button */}
              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="sm" color="white" />
                  ) : (
                    'Iniciar Sesión'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-gray-600">
          © {new Date().getFullYear()} Teonila HouseKeeping Management. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default Login;